<template>
	<div :class="['goods-detail-page', isSelfGoods ? 'is-self-goods' : 'is-exhibition-goods']">
		<div class="detail-container">
			<div class="breadcrumb-wrap">
				<el-breadcrumb separator-class="el-icon-arrow-right">
					<el-breadcrumb-item :to="{ path: '/mallStore/index' }">服务商城</el-breadcrumb-item>
					<el-breadcrumb-item>{{ isSelfGoods ? '平台自营' : '展销商品' }}</el-breadcrumb-item>
					<el-breadcrumb-item>商品详情</el-breadcrumb-item>
				</el-breadcrumb>
			</div>

			<section class="goods-overview">
				<div class="main-image-wrap">
					<img :src="goods.mainImage || defaultImage" :alt="goods.title" @error="handleImageError" />
					<span :class="['type-badge', isSelfGoods ? 'is-self' : 'is-exhibition']">
						<i :class="isSelfGoods ? 'el-icon-s-check' : 'el-icon-s-promotion'"></i>
						{{ isSelfGoods ? '平台自营商品' : '优选展销商品' }}
					</span>
				</div>

				<div class="goods-meta">
					<div class="meta-topline">
						<span>{{ goods.shopName || (isSelfGoods ? '平台服务 · 在线交易' : '服务商提供 · 专属咨询') }}</span>
					</div>
					<h1 :title="goods.title">{{ goods.title }}</h1>
					<p class="goods-introduction" :title="goods.introduction">{{ goods.introduction }}</p>

					<div class="goods-attribute-panel">
						<div class="attribute-row">
							<span class="attribute-label">所属类目</span>
							<span class="category-tag">
								<i class="el-icon-collection-tag"></i>
								{{ goods.category }}
							</span>
						</div>
						<div v-if="isSelfGoods" class="attribute-row price-row">
							<span class="attribute-label">商品价格</span>
							<div class="price-value">
								<small>{{ goods.priceUnit === '元' ? '¥' : goods.priceUnit }}</small>
								<strong>{{ goods.price }}</strong>
								<span>{{ goods.priceUnit || '元' }} / {{ goods.measureUnit || '件' }}</span>
							</div>
						</div>
						<div v-else class="attribute-row">
							<span class="attribute-label">服务方式</span>
							<span class="consult-mode">
								<i class="el-icon-chat-dot-round"></i>
								提交咨询后，由服务商与您对接需求
							</span>
						</div>
						<div v-if="goods.deliveryText" class="attribute-row">
							<span class="attribute-label">发货时效</span>
							<span class="delivery-value">{{ goods.deliveryText }}</span>
						</div>
						<div v-if="goods.deliveryAgreement" class="attribute-row">
							<span class="attribute-label">发货约定</span>
							<span class="delivery-value">{{ goods.deliveryAgreement }}</span>
						</div>
					</div>

					<div v-if="!isSelfGoods" class="contact-card">
						<span class="contact-icon"><i class="el-icon-service"></i></span>
						<div>
							<strong>专属服务顾问</strong>
							<p>{{ goods.contactName || '商城服务顾问' }} · {{ goods.contactPhone || '提交咨询后与您联系' }}</p>
						</div>
					</div>

					<div class="action-area">
						<template v-if="isSelfGoods">
							<el-button class="cart-button" :loading="cartLoading" :disabled="cartLoading" @click="$emit('add-cart')">
								<i class="el-icon-shopping-cart-2"></i>
								加入购物车
							</el-button>
							<el-button
								type="primary"
								class="primary-button"
								:loading="buyLoading"
								:disabled="buyLoading"
								@click="$emit('buy-now')">
								立即购买
							</el-button>
						</template>
						<el-button v-else type="primary" class="primary-button consult-button" @click="$emit('consult')">
							<i class="el-icon-service"></i>
							立即咨询
						</el-button>
					</div>
					<div class="service-promise">
						<span>
							<i class="el-icon-circle-check"></i>
							平台甄选
						</span>
						<span>
							<i class="el-icon-circle-check"></i>
							专业服务
						</span>
						<span>
							<i class="el-icon-circle-check"></i>
							{{ isSelfGoods ? '在线购买' : '顾问对接' }}
						</span>
					</div>
				</div>
			</section>

			<section class="detail-section">
				<header class="detail-heading">
					<div>
						<span>PRODUCT DETAILS</span>
						<h2>商品详情</h2>
					</div>
					<p>了解服务内容、使用场景及交付说明</p>
				</header>
				<div class="rich-text-content" v-html="goods.detailContent"></div>
			</section>
		</div>
	</div>
</template>

<script>
	const DEFAULT_IMAGE = require('@/assets/mall/goods/goods-demo.png')

	export default {
		name: 'MallStoreGoodsDetailView',
		props: {
			goodsType: {
				type: Number,
				required: true
			},
			goods: {
				type: Object,
				required: true
			},
			cartLoading: {
				type: Boolean,
				default: false
			},
			buyLoading: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				defaultImage: DEFAULT_IMAGE
			}
		},
		computed: {
			isSelfGoods() {
				return this.goodsType === 1
			}
		},
		methods: {
			handleImageError(event) {
				event.target.src = DEFAULT_IMAGE
			}
		}
	}
</script>

<style lang="scss" scoped>
	.goods-detail-page {
		--detail-theme: #176fc8;
		--detail-theme-deep: #0f5ead;
		--detail-theme-soft: #eaf4ff;
		--detail-theme-border: #cfe4f8;
		--detail-theme-shadow: rgba(23, 111, 200, 0.22);
		position: relative;
		min-height: 720px;
		padding: 24px 0 64px;
		overflow: hidden;
		color: #253244;
		background: radial-gradient(circle at 8% 8%, rgba(95, 172, 237, 0.16), transparent 25%),
			radial-gradient(circle at 92% 31%, rgba(123, 190, 241, 0.12), transparent 24%),
			linear-gradient(180deg, #f5f9fd 0%, #edf5fb 52%, #f8fbfe 100%);

		&::before,
		&::after {
			position: absolute;
			border: 1px solid rgba(255, 255, 255, 0.8);
			border-radius: 50%;
			content: '';
			pointer-events: none;
		}

		&::before {
			top: 120px;
			left: -150px;
			width: 360px;
			height: 360px;
		}

		&::after {
			right: -130px;
			bottom: 100px;
			width: 300px;
			height: 300px;
		}

		&.is-exhibition-goods {
			--detail-theme: #e98525;
			--detail-theme-deep: #cf6f17;
			--detail-theme-soft: #fff4e8;
			--detail-theme-border: #f5d5b4;
			--detail-theme-shadow: rgba(233, 133, 37, 0.24);
			background: radial-gradient(circle at 8% 8%, rgba(255, 189, 112, 0.19), transparent 25%),
				radial-gradient(circle at 92% 31%, rgba(255, 205, 149, 0.14), transparent 24%),
				linear-gradient(180deg, #fdf9f4 0%, #f9f3eb 52%, #fdfbf8 100%);
		}
	}

	.detail-container {
		position: relative;
		z-index: 1;
		width: $main-page-width;
		margin: 0 auto;
	}

	.breadcrumb-wrap {
		display: inline-flex;
		align-items: center;
		margin-bottom: 18px;
		padding: 10px 15px;
		border: 1px solid rgba(210, 225, 239, 0.82);
		border-radius: 20px;
		background: rgba(255, 255, 255, 0.72);
		box-shadow: 0 4px 14px rgba(55, 89, 122, 0.05);
		backdrop-filter: blur(6px);

		::v-deep .el-breadcrumb__inner {
			color: #627388;
			font-size: 14px;
		}

		::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner {
			color: var(--detail-theme);
			font-weight: 600;
		}
	}

	.goods-overview {
		position: relative;
		display: flex;
		padding: 32px;
		overflow: hidden;
		border: 1px solid rgba(218, 229, 239, 0.9);
		border-radius: 20px;
		background: linear-gradient(135deg, rgba(255, 255, 255, 0.99), rgba(252, 254, 255, 0.95));
		box-shadow: 0 18px 46px rgba(44, 74, 103, 0.1);

		&::after {
			position: absolute;
			top: -120px;
			right: -100px;
			width: 300px;
			height: 300px;
			border-radius: 50%;
			background: radial-gradient(circle, var(--detail-theme-soft), transparent 68%);
			content: '';
			pointer-events: none;
		}
	}

	.main-image-wrap {
		position: relative;
		z-index: 1;
		flex: 0 0 480px;
		height: 430px;
		overflow: hidden;
		border: 1px solid var(--detail-theme-border);
		border-radius: 16px;
		background: linear-gradient(145deg, #f6f8fb, #e9eff6);
		box-shadow: 0 12px 30px rgba(41, 69, 97, 0.1);

		img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
			transition: transform 0.45s ease;
		}

		&:hover img {
			transform: scale(1.025);
		}
	}

	.type-badge {
		position: absolute;
		top: 18px;
		left: 18px;
		padding: 9px 14px;
		border: 1px solid rgba(255, 255, 255, 0.52);
		border-radius: 18px;
		color: #ffffff;
		font-size: 14px;
		box-shadow: 0 7px 18px var(--detail-theme-shadow);
		backdrop-filter: blur(4px);

		i {
			margin-right: 5px;
		}

		&.is-self {
			background: linear-gradient(135deg, #318cdd, #126ec9);
		}

		&.is-exhibition {
			background: linear-gradient(135deg, #f4a550, #e47b1c);
		}
	}

	.goods-meta {
		position: relative;
		z-index: 1;
		flex: 1;
		min-width: 0;
		padding: 3px 8px 0 44px;
	}

	.meta-topline {
		display: flex;
		align-items: center;
		font-size: 14px;

		span {
			display: inline-flex;
			align-items: center;
			padding: 6px 12px;
			border: 1px solid var(--detail-theme-border);
			border-radius: 16px;
			color: var(--detail-theme);
			font-weight: 500;
			background: var(--detail-theme-soft);
		}
	}

	h1 {
		margin: 17px 0 12px;
		color: #1f2b3a;
		font-size: 30px;
		line-height: 1.4;
		word-break: break-word;
	}

	.goods-introduction {
		margin: 0 0 20px;
		padding-left: 12px;
		border-left: 3px solid var(--detail-theme-border);
		color: #687588;
		font-size: 15px;
		line-height: 25px;
		word-break: break-word;
	}

	.goods-attribute-panel {
		padding: 10px 20px;
		border: 1px solid #e8eef4;
		border-radius: 12px;
		background: linear-gradient(135deg, #f8fafc, #f4f7fa);
	}

	.attribute-row {
		display: flex;
		align-items: center;
		min-height: 54px;
		font-size: 14px;

		& + & {
			border-top: 1px dashed #dfe5ec;
		}
	}

	.attribute-label {
		flex: 0 0 84px;
		color: #7d8795;
		font-weight: 500;
	}

	.category-tag {
		padding: 6px 12px;
		border: 1px solid var(--detail-theme-border);
		border-radius: 16px;
		color: var(--detail-theme-deep);
		background: var(--detail-theme-soft);

		i {
			margin-right: 5px;
		}
	}

	.price-value {
		color: #ed6624;

		small {
			margin-right: 3px;
			font-size: 16px;
		}

		strong {
			font-size: 30px;
		}

		span {
			margin-left: 7px;
			color: #8993a0;
			font-size: 14px;
		}
	}

	.consult-mode {
		flex: 1;
		min-width: 0;
		color: #4d5d70;
		line-height: 22px;

		i {
			margin-right: 6px;
			color: var(--detail-theme);
			font-size: 18px;
		}
	}

	.delivery-value {
		flex: 1;
		min-width: 0;
		color: #4d5d70;
		line-height: 22px;
		word-break: break-word;
	}

	.contact-card {
		display: flex;
		align-items: center;
		margin-top: 18px;
		padding: 14px 18px;
		border: 1px solid var(--detail-theme-border);
		border-radius: 12px;
		background: linear-gradient(90deg, var(--detail-theme-soft), #ffffff);
		box-shadow: 0 7px 18px rgba(129, 94, 54, 0.06);

		.contact-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			flex: 0 0 42px;
			height: 42px;
			margin-right: 12px;
			border-radius: 50%;
			color: #ffffff;
			font-size: 21px;
			background: linear-gradient(135deg, #f4a550, var(--detail-theme));
			box-shadow: 0 6px 14px var(--detail-theme-shadow);
		}

		strong {
			font-size: 15px;
		}

		p {
			margin: 5px 0 0;
			color: #7b6c5e;
			font-size: 14px;
		}
	}

	.action-area {
		display: flex;
		margin-top: 26px;

		.el-button {
			height: 48px;
			min-width: 165px;
			border-radius: 10px;
			font-size: 16px;
			transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

			&:not(.is-disabled):hover {
				transform: translateY(-2px);
			}

			&:focus-visible {
				outline: 2px solid var(--detail-theme-border);
				outline-offset: 2px;
			}
		}

		.cart-button {
			border-color: #f09b55;
			color: #df721f;
			background: #fff8f1;

			&:hover {
				border-color: #e98632;
				background: #fff1e4;
				box-shadow: 0 8px 18px rgba(233, 133, 37, 0.16);
			}
		}

		.primary-button {
			margin-left: 14px;
			border-color: var(--detail-theme);
			background: linear-gradient(100deg, var(--detail-theme), var(--detail-theme-deep));
			box-shadow: 0 8px 18px var(--detail-theme-shadow);
		}

		.consult-button {
			min-width: 205px;
			margin-left: 0;
			border-color: var(--detail-theme);
			background: linear-gradient(100deg, var(--detail-theme), #f2a34c);
		}
	}

	.service-promise {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		margin-top: 17px;
		color: #788493;

		span {
			padding: 6px 10px;
			border-radius: 15px;
			font-size: 14px;
			background: #f5f8fb;
		}

		i {
			margin-right: 5px;
			color: #39a56b;
		}
	}

	.detail-section {
		margin-top: 28px;
		overflow: hidden;
		border: 1px solid #e4eaf1;
		border-radius: 20px;
		background: #ffffff;
		box-shadow: 0 14px 38px rgba(51, 78, 110, 0.08);
	}

	.detail-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 25px 34px;
		border-bottom: 1px solid #e7edf3;
		background: linear-gradient(90deg, var(--detail-theme-soft), #ffffff 68%);

		> div {
			position: relative;
			padding-left: 15px;

			&::before {
				position: absolute;
				top: 3px;
				bottom: 2px;
				left: 0;
				width: 4px;
				border-radius: 2px;
				background: var(--detail-theme);
				content: '';
			}
		}

		span {
			color: var(--detail-theme);
			font-size: 14px;
			letter-spacing: 1px;
		}

		h2 {
			margin: 4px 0 0;
			font-size: 22px;
		}

		p {
			margin: 0;
			color: #8994a2;
			font-size: 14px;
		}
	}

	.rich-text-content {
		min-height: 280px;
		padding: 40px 50px 52px;
		overflow: hidden;
		color: #455365;
		font-size: 15px;
		line-height: 1.9;
		word-break: break-word;

		::v-deep {
			* {
				max-width: 100%;
				box-sizing: border-box;
			}

			h2,
			h3,
			h4 {
				color: #253447;
			}

			h2 {
				margin: 4px 0 22px;
				font-size: 24px;
			}

			h3 {
				margin: 30px 0 14px;
				padding-left: 12px;
				border-left: 4px solid var(--detail-theme);
				font-size: 18px;
			}

			p,
			li,
			td,
			th {
				font-size: 15px;
				line-height: 1.9;
			}

			img,
			video {
				display: block;
				height: auto !important;
				margin: 18px auto;
			}

			table {
				width: 100% !important;
				border-collapse: collapse;
			}

			th {
				color: #33465b;
				background: var(--detail-theme-soft);
			}

			a {
				color: var(--detail-theme);
			}

			td,
			th {
				padding: 10px 12px;
				border: 1px solid #dfe5ec;
			}
		}
	}

	@media (max-width: 1400px) {
		.detail-container {
			width: calc(100% - 48px);
		}

		.main-image-wrap {
			flex-basis: 40%;
		}
	}

	@media (max-width: 1050px) {
		.goods-overview {
			flex-direction: column;
		}

		.main-image-wrap {
			width: 100%;
			height: 430px;
			flex-basis: auto;
		}

		.goods-meta {
			padding: 30px 4px 0;
		}
	}

	@media (max-width: 768px) {
		.goods-detail-page {
			padding: 16px 0 40px;
		}

		.detail-container {
			width: calc(100% - 24px);
		}

		.goods-overview {
			padding: 18px;
			border-radius: 16px;
		}

		.main-image-wrap {
			height: 320px;
		}

		h1 {
			font-size: 24px;
		}

		.attribute-row {
			align-items: flex-start;
			padding: 12px 0;
		}

		.action-area .el-button {
			flex: 1;
			min-width: 0;
		}

		.detail-heading {
			align-items: flex-start;
			flex-direction: column;
			gap: 10px;
			padding: 22px;
		}

		.rich-text-content {
			padding: 28px 22px 36px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.main-image-wrap img,
		.action-area .el-button {
			transition: none;
		}

		.main-image-wrap:hover img,
		.action-area .el-button:not(.is-disabled):hover {
			transform: none;
		}
	}
</style>
